import { Filter, Users } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import type { HunterStat } from '@/shared/domain'
import { DataTable } from './data-table'
import { EmptyState } from './empty-state'
import { ErrorState } from './error-state'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/primitives'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/shared/ui/primitives/card'
import { Skeleton } from '@/shared/ui/primitives/skeleton'

export type TopHuntersTimeFilter = 'season' | 'month' | 'week'

type TopHuntersCardProps = {
  hunters: HunterStat[]
  isLoading: boolean
  isHistoricalSeason?: boolean
  error?: boolean
  timeFilter?: TopHuntersTimeFilter
  onTimeFilterChange?: (filter: TopHuntersTimeFilter) => void
}

const columns: ColumnDef<HunterStat, unknown>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    enableSorting: false,
    meta: { cellClassName: 'font-medium' },
  },
  {
    accessorKey: 'totalHarvest',
    header: 'Harvest',
    meta: { cellClassName: 'font-mono' },
  },
  {
    accessorKey: 'huntsCount',
    header: 'Hunts',
    meta: { cellClassName: 'font-mono' },
  },
  {
    accessorKey: 'averagePerHunt',
    header: 'Avg',
    cell: ({ getValue }) => getValue<number>().toFixed(1),
    meta: { cellClassName: 'font-mono' },
  },
]

const TIME_FILTER_LABELS: Record<TopHuntersTimeFilter, string> = {
  season: 'Season',
  month: 'This Month',
  week: 'This Week',
}

function TopHuntersSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  )
}

export function TopHuntersCard({
  hunters,
  isLoading,
  isHistoricalSeason = false,
  error = false,
  timeFilter,
  onTimeFilterChange,
}: TopHuntersCardProps) {
  const showFilter = timeFilter !== undefined && onTimeFilterChange !== undefined

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Hunters</CardTitle>
        {showFilter ? (
          <CardAction>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 size-4" />
                  Filters
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 space-y-3">
                <section className="space-y-2">
                  <h4 className="text-sm font-semibold">Period</h4>
                  <Select
                    value={timeFilter}
                    onValueChange={(v) => onTimeFilterChange(v as TopHuntersTimeFilter)}
                  >
                    <SelectTrigger aria-label="Period filter">
                      <SelectValue>{TIME_FILTER_LABELS[timeFilter]}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="season">Season</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                    </SelectContent>
                  </Select>
                </section>
              </PopoverContent>
            </Popover>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <TopHuntersSkeleton />
        ) : error ? (
          <ErrorState title="Hunters unavailable" description="Unable to load top hunters." />
        ) : isHistoricalSeason ? (
          <EmptyState
            icon={Users}
            heading="Hunter stats not available for historical seasons"
            description="Switch to a regular season to see top hunters."
          />
        ) : hunters.length === 0 ? (
          <EmptyState
            icon={Users}
            heading="No hunt data"
            description="No hunts recorded for this period."
          />
        ) : (
          <DataTable
            columns={columns}
            data={hunters}
            enableSorting
            initialSorting={[{ id: 'totalHarvest', desc: true }]}
            enablePagination
            enableRowNumbers
            pageSize={10}
            renderMobileCard={(hunter, { rank }) => (
              <div className="flex items-start gap-3">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-xs font-semibold">
                  {rank}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{hunter.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      Harvest <span className="font-mono text-foreground">{hunter.totalHarvest}</span>
                    </span>
                    <span>
                      Hunts <span className="font-mono text-foreground">{hunter.huntsCount}</span>
                    </span>
                    <span>
                      Avg{' '}
                      <span className="font-mono text-foreground">
                        {hunter.averagePerHunt.toFixed(1)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          />
        )}
      </CardContent>
    </Card>
  )
}
